import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, Upload, X, Image as ImageIcon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import TermsCheckbox from "@/components/TermsCheckbox";

export default function Register() {
    const { register, setUser } = useAuth();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", company: "", email: "", marketing_email: "", phone: "", password: "" });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [pendingMessage, setPendingMessage] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onLogoSelect = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setLogoFile(f);
        setLogoPreview(URL.createObjectURL(f));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        let logo_url = null;
        if (logoFile) {
            try {
                const fd = new FormData();
                fd.append("file", logoFile);
                const { data: up } = await api.post("/upload/public-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                logo_url = up.url;
            } catch (err) {
                toast.warning(t("auth.register.toast_logo_failed"));
            }
        }
        let signupUtm = null;
        try { signupUtm = JSON.parse(localStorage.getItem("signup_utm") || "null"); } catch (e) { /* noop */ }
        const res = await register({ ...form, logo_url, accepted_terms: acceptedTerms, preferred_language: (i18n.language || "nl").slice(0, 2), signup_utm: signupUtm });
        setLoading(false);
        if (!res.ok) {
            toast.error(res.error);
            return;
        }
        if (res.pending) {
            try { window.oaiq?.("measure", "registration_completed", { type: "customer_action" }); } catch (e) { /* noop */ }
            setPendingMessage(res.message);
            return;
        }
        try { window.oaiq?.("measure", "registration_completed", { type: "customer_action" }); } catch (e) { /* noop */ }
        toast.success(t("auth.register.toast_created"));
        // Conversion funnel: land on the first-win chooser so they immediately
        // use their 10 welcome credits or free AI video tour.
        navigate("/start", { replace: true });
    };

    if (pendingMessage) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="register-pending-screen">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-md w-full bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md p-8 lg:p-10 text-center"
                >
                    <div className="h-14 w-14 rounded-full bg-[#E5A93D]/15 border border-[#E5A93D]/40 flex items-center justify-center mx-auto mb-6">
                        <Clock className="h-6 w-6 text-[#E5A93D]" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] mb-3">{t("auth.register.pending_eyebrow")}</div>
                    <h1 className="font-display font-black text-3xl tracking-tighter mb-4 text-zinc-950">{t("auth.register.pending_title")}</h1>
                    <p className="text-sm text-zinc-500 leading-relaxed mb-8">{pendingMessage}</p>
                    <div className="flex flex-col gap-2">
                        <Link to="/" className="inline-flex items-center justify-center gap-2 h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98] transition" data-testid="pending-to-home">
                            {t("auth.register.pending_to_home")}
                        </Link>
                        <Link to="/login" className="text-sm text-zinc-500 hover:text-zinc-950 transition py-2" data-testid="pending-to-login">
                            {t("auth.register.pending_to_login")}
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="register-page">
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFFFFF] via-[#FAFAFA] to-[#EFF6FF]" />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: `url(https://images.pexels.com/photos/30725651/pexels-photo-30725651.jpeg)`,
                        backgroundSize: "cover", backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-[#FAFAFA]/60" />
                <div className="relative flex flex-col justify-between p-12 z-10">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="/social-factory-logo.png"
                            alt="Social Factory"
                            className="h-12 w-12 object-contain invert"
                        />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Social Factory</div>
                            <div className="text-sm font-bold text-zinc-950">{t("common.brand_studio")}</div>
                        </div>
                    </Link>
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] mb-4">{t("auth.register.side_eyebrow")}</div>
                        <h2 className="font-display font-black text-4xl tracking-tighter leading-none max-w-md text-balance text-zinc-950">
                            {t("auth.register.side_title_1")} <span className="italic font-medium text-[#E5A93D]">{t("auth.register.side_title_highlight")}</span> {t("auth.register.side_title_2")}
                        </h2>
                        <p className="text-zinc-500 mt-4 max-w-md text-sm leading-relaxed">
                            {t("auth.register.side_subtitle")}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 relative">
                <Link to="/" className="absolute top-6 left-6 text-zinc-500 hover:text-zinc-950 text-sm flex items-center gap-2" data-testid="back-home">
                    <ArrowLeft className="h-4 w-4" /> {t("auth.register.back")}
                </Link>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full max-w-md py-10"
                >
                    <div className="mb-8">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] mb-3">{t("auth.register.form_eyebrow")}</div>
                        <h1 className="font-display font-black text-4xl tracking-tighter">{t("auth.register.form_title")}</h1>
                    </div>
                    <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.name_label")}</Label>
                            <Input required value={form.name} onChange={set("name")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11" data-testid="reg-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.company_label")}</Label>
                            <Input value={form.company} onChange={set("company")} placeholder={t("auth.register.company_placeholder")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11" data-testid="reg-company-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.email_label")}</Label>
                            <Input type="email" required value={form.email} onChange={set("email")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11" data-testid="reg-email-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.marketing_email_label")}</Label>
                            <p className="text-[11px] text-zinc-500 mb-2">{t("auth.register.marketing_email_help")}</p>
                            <Input type="email" value={form.marketing_email} onChange={set("marketing_email")} placeholder={t("auth.register.marketing_email_placeholder")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11" data-testid="reg-marketing-email-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.phone_label")}</Label>
                            <Input value={form.phone} onChange={set("phone")} placeholder={t("auth.register.phone_placeholder")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11" data-testid="reg-phone-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.password_label")}</Label>
                            <Input type="password" required minLength={6} value={form.password} onChange={set("password")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11" data-testid="reg-password-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.register.logo_label")}</Label>
                            <p className="text-[11px] text-zinc-500 mb-2">{t("auth.register.logo_help")}</p>
                            <label className="flex items-center gap-3 p-3 border-2 border-dashed border-zinc-200 hover:border-[#E5A93D]/50 rounded-md cursor-pointer transition-colors" data-testid="reg-logo-upload">
                                {logoPreview ? (
                                    <div className="flex items-center gap-3 flex-1">
                                        {logoFile?.type?.startsWith("image/") ? (
                                            <img src={logoPreview} alt="logo" className="h-12 w-12 object-contain bg-zinc-100 rounded-sm" />
                                        ) : (
                                            <div className="h-12 w-12 bg-[#E5A93D]/10 flex items-center justify-center rounded-sm">
                                                <ImageIcon className="h-5 w-5 text-[#E5A93D]" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-zinc-950 truncate">{logoFile?.name}</div>
                                            <div className="text-xs text-zinc-500">{t("auth.register.logo_replace")}</div>
                                        </div>
                                        <button type="button" onClick={(e) => { e.preventDefault(); setLogoFile(null); setLogoPreview(null); }} className="text-zinc-500 hover:text-zinc-950">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                                        <Upload className="h-4 w-4" />
                                        <span>{t("auth.register.logo_choose")}</span>
                                    </div>
                                )}
                                <input type="file" accept="image/png,image/svg+xml,image/*,application/pdf,.ai,.eps" className="hidden" onChange={onLogoSelect} />
                            </label>
                        </div>
                        <div className="pt-1">
                            <TermsCheckbox checked={acceptedTerms} onChange={setAcceptedTerms} />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold tracking-wide rounded-sm active:scale-[0.98] mt-2" data-testid="register-submit-btn">
                            {loading ? t("auth.register.submitting") : t("auth.register.submit")}
                        </Button>
                    </form>
                    <div className="mt-6 text-sm text-zinc-500 text-center">
                        {t("auth.register.have_account")} <Link to="/login" className="text-[#E5A93D] hover:underline" data-testid="to-login-link">{t("auth.register.login_link")}</Link>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
